import { getLogger } from '@packages/core/logging';

import type { FailCodeEntity, FailCodePartEntity } from '../entities';

import { StsBackendOperation } from '../../sts-backend-operation';

const logger = getLogger('fail-codes');

/**
 * Parameters for searching fail codes by keyword query.
 */
export type SearchFailCodesParams = {
    /** The vehicle model code (e.g. "GUE"). */
    modelCode?: string;

    /** The vehicle model year (e.g. "2024"). */
    modelYear?: string;

    /** The vehicle option/trim code (e.g. "01"). */
    optionCode?: string;

    /** The search query string to match against fail codes. */
    query?: string;

    /** Optional abort signal for cancellation. */
    signal?: AbortSignal;
};

/**
 * The result of a fail code search, containing both matching fail codes and parts.
 */
export type SearchFailCodesResult = {
    /** The list of fail codes matching the search query. */
    failCodes: FailCodeEntity[];

    /** The list of parts matching the search query. */
    parts: FailCodePartEntity[];
};

/**
 * Search for fail codes by query text, scoped to a specific vehicle configuration.
 * Used by the search-by-fail-code flow in the fail codes modal.
 */
export class SearchFailCodes extends StsBackendOperation {
    /**
     * Search fail codes from the API and return them as domain entities.
     *
     * @param params - the vehicle identifiers, search query, and optional abort signal
     * @returns the matching fail codes and parts as domain entities
     */
    async exec(params: SearchFailCodesParams): Promise<SearchFailCodesResult> {
        const { signal, ...request } = params;
        try {
            const response = await this.client.searchFailCodes(request, signal);

            if (!response.success) {
                return { failCodes: [], parts: [] };
            }

            return {
                failCodes: response.data.failCodes ?? [],
                parts: response.data.parts ?? [],
            };
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Failed to search fail codes';
            logger.error('Failed to search the fail codes', { error: e });
            throw new Error(message);
        }
    }
}
